import React, { useEffect, useState } from 'react'
import { Button, Card, Input, Modal, Typography, notification } from 'antd'

import { getCurrentUser } from '~context'
import { PageLayout, Spinner } from '~stories'
import { fetchLatestBid, updateBid } from '~utils/auctionApi'

import styles from './AuctionAdmin.module.css'

const PAGE_TITLE = 'Celebrity Row Auction Admin'
const { REACT_APP_STAGE } = process.env

function formatCurrency (amount) {
  if (amount == null) return '—'
  return `$${Number(amount).toLocaleString()}`
}

function formatDate (isoString) {
  if (!isoString) return null
  return new Date(isoString).toLocaleString('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
    timeZone: 'America/New_York'
  })
}

function getErrorMessage (err) {
  if (err instanceof TypeError) {
    return 'Network error. Check your connection and try again.'
  }

  return err.message || 'Something went wrong. Please try again.'
}

const AuctionAdmin = () => {
  const { userDetails: { isContentEditor, userName } = {} } = getCurrentUser()

  const [bidAmount, setBidAmount] = useState('')
  const [currentBid, setCurrentBid] = useState(null)
  const [updatedAt, setUpdatedAt] = useState(null)
  const [updatedBy, setUpdatedBy] = useState(null)
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(false)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  const [pendingAmount, setPendingAmount] = useState(null)

  useEffect(() => {
    if (REACT_APP_STAGE !== 'staging' || !isContentEditor) return

    setFetching(true)
    fetchLatestBid()
      .then(data => {
        setCurrentBid(data.bidAmount)
        setUpdatedAt(data.updatedAt)
        setUpdatedBy(data.updatedBy)
        setBidAmount(String(data.bidAmount))
      })
      .catch(err => {
        notification.error({ message: getErrorMessage(err) })
      })
      .finally(() => setFetching(false))
  }, [isContentEditor])

  if (REACT_APP_STAGE !== 'staging') {
    return (
      <PageLayout title={PAGE_TITLE}>
        <Card className={styles.card}>
          <Typography.Text type="secondary">
            Celebrity Row Auction admin is only available in the staging environment.
          </Typography.Text>
        </Card>
      </PageLayout>
    )
  }

  const handleSubmit = () => {
    const parsedAmount = Number(String(bidAmount).replace(/,/g, ''))

    if (Number.isNaN(parsedAmount) || parsedAmount <= 0) {
      notification.error({ message: 'Enter a valid bid amount greater than zero.' })
      return
    }

    if (currentBid != null && parsedAmount === currentBid) {
      notification.warning({ message: 'New bid amount matches the current bid.' })
      return
    }

    setPendingAmount(parsedAmount)
    setShowConfirmModal(true)
  }

  const handleConfirmUpdate = async () => {
    if (pendingAmount == null) return

    setShowConfirmModal(false)
    setLoading(true)

    try {
      const data = await updateBid({
        email: userName,
        bidAmount: pendingAmount
      })

      setCurrentBid(data.bidAmount)
      setUpdatedAt(data.updatedAt)
      setUpdatedBy(data.updatedBy)
      setBidAmount(String(data.bidAmount))
      notification.success({ message: `Bid updated to ${formatCurrency(data.bidAmount)}.` })
    } catch (err) {
      notification.error({ message: getErrorMessage(err) })
    } finally {
      setLoading(false)
      setPendingAmount(null)
    }
  }

  if (!isContentEditor) {
    return (
      <PageLayout title={PAGE_TITLE}>
        <Card className={styles.card}>
          <Typography.Text type="danger">
            You are not authorized to manage the Celebrity Row Courtside auction bid.
          </Typography.Text>
        </Card>
      </PageLayout>
    )
  }

  if (fetching) return <Spinner isLoading />

  return (
    <PageLayout title={PAGE_TITLE}>
      <Typography.Paragraph className={styles.subhead}>
        Update the Celebrity Row Courtside auction bid amount. Signed in as {userName}.
      </Typography.Paragraph>

      <Card className={styles.card} title="Current Auction Bid">
        <div className={styles.currentBid}>
          <div className={styles.currentBidLabel}>Current Bid</div>
          <div className={styles.currentBidAmount}>
            {fetching ? '...' : formatCurrency(currentBid)}
          </div>
        </div>

        <dl className={styles.meta}>
          <div className={styles.metaRow}>
            <dt className={styles.metaLabel}>Last modified</dt>
            <dd className={styles.metaValue}>
              {fetching ? '...' : (updatedAt ? `${formatDate(updatedAt)} ET` : '—')}
            </dd>
          </div>
          <div className={styles.metaRow}>
            <dt className={styles.metaLabel}>Updated by</dt>
            <dd className={styles.metaValue}>
              {fetching ? '...' : (updatedBy || '—')}
            </dd>
          </div>
        </dl>

        <Typography.Text strong>New Bid Amount (USD)</Typography.Text>
        <Input
          min={1}
          onChange={e => setBidAmount(e.target.value)}
          step={1}
          style={{ marginTop: 8, marginBottom: 16 }}
          type="number"
          value={bidAmount}
        />

        <Button
          disabled={loading || fetching}
          loading={loading}
          onClick={handleSubmit}
          type="primary"
        >
          Update Bid
        </Button>
      </Card>

      <Modal
        cancelText="Cancel"
        okText="Confirm & Update"
        onCancel={() => {
          setShowConfirmModal(false)
          setPendingAmount(null)
        }}
        onOk={handleConfirmUpdate}
        title="Confirm Bid Update"
        visible={showConfirmModal}
      >
        Update the current bid from{' '}
        <strong>{formatCurrency(currentBid)}</strong> to{' '}
        <strong>{formatCurrency(pendingAmount)}</strong>?
      </Modal>
    </PageLayout>
  )
}

export default AuctionAdmin
